import {getCountryCodeNoneFiltered} from '../CountryRestriction/CountryRestrictionUtility';
import SlimSelect from 'slim-select';
import {getSearchLabel} from '../Controllers/SlimSelectController';

export class Flyout {
  private readonly flyoutOpenClass = 'contactsFlyoutWrapper--active';

  private hideClass: string = 'hide';
  private contactElements: NodeList | null = null;
  private countrySelect: HTMLSelectElement | null = null;
  private countrySelectMobile: HTMLSelectElement | null = null;
  private zipInput: HTMLInputElement | null = null;
  private zipInputMobile: HTMLInputElement | null = null;
  private readonly buttonElements: NodeListOf<Element>;
  private readonly wrapperElement: HTMLElement | null = null;
  private worldwideLinkElement: HTMLAnchorElement | null = null;

  private selectedCountry: string = '';
  private zipInputCountries: Array<string> = ['de', 'ch'];

  private departmentId: string = '';
  private defaultWorldwideLink: string = '';

  private zipInputWasUsed: boolean = false;

  constructor() {
    this.buttonElements = document.querySelectorAll('.js-flyout-button');
    this.wrapperElement = document.querySelector('.js-flyout-wrapper');
    this.countrySelectMobile = document.querySelector('.js-flyout-countryselect-mobile');
    this.zipInputMobile = document.querySelector('.js-flyout-zipinput-mobile');

    if (this.wrapperElement) {
      this.departmentId = this.wrapperElement.dataset.department || '';
    }
  }

  public initButtonListeners() {
    this.applyButtonElementsListeners();
  }

  public initMobileContactFlyout() {
    const wrapper = this.countrySelectMobile?.parentElement;
    if (!wrapper) {
      return;
    }
    this.departmentId = wrapper.dataset.department || '';
    this.contactElements = wrapper.querySelectorAll('.js-flyout-contact');

    this.applyMobileListeners();
    this.applyCurrentCountry();
    this.initSlimSelect();
    this.showCorrectContactElements(this.zipInputMobile, this.countrySelectMobile);
    this.handleProductInquiryButton();
    this.updateWorldwideLink();
  }

  private wrapperElementInit() {
    if (!this.wrapperElement?.innerHTML.includes('<!--js-contactsFlyout')) {
      return;
    }
    this.unzip(this.wrapperElement);

    this.contactElements = this.wrapperElement.querySelectorAll('.js-flyout-contact');
    this.zipInput = document.querySelector('.js-flyout-zipinput');
    this.countrySelect = document.querySelector('.js-flyout-countryselect');
    this.worldwideLinkElement = document.querySelector('.js-worldwide-link');

    if (this.worldwideLinkElement) {
      this.defaultWorldwideLink = this.worldwideLinkElement.href;
    }

    if (!this.countrySelect || !this.zipInput) {
      return;
    }

    this.applyListeners();
    this.applyCurrentCountry();
    this.initSlimSelect();
    this.showCorrectContactElements(this.zipInput, this.countrySelect);
    this.handleProductInquiryButton();
    this.updateWorldwideLink();
  }

  private applyButtonElementsListeners(): void {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.js-flyout-container')) {
        if (this.wrapperElement!.classList.contains(this.flyoutOpenClass)) {
          this.wrapperElement!.classList.remove(this.flyoutOpenClass);
        }
      }
    });

    if (this.buttonElements && this.wrapperElement) {
      this.buttonElements.forEach((button) => {
        button.addEventListener('click', () => {
          this.wrapperElementInit();
          this.wrapperElement!.classList.toggle(this.flyoutOpenClass);
        });
      });

    }
  }

  private applyListeners(): void {
    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', (e: Event) => {
        this.showCorrectContactElements(this.zipInput, this.countrySelect);
        this.updateWorldwideLink();
      });
    }

    if (!this.contactElements) {
      return;
    }

    if (this.zipInput) {
      this.zipInput.addEventListener('input', () => {
        this.handleZipInput(this.zipInput, this.countrySelect);
      });
    }
  }

  private applyMobileListeners() {
    if (this.countrySelectMobile) {
      this.countrySelectMobile.addEventListener('change', (e: Event) => {
        this.displayMobileContactFlyoutImage(this.countrySelectMobile);
        this.showCorrectContactElements(this.zipInputMobile, this.countrySelectMobile);
        this.updateWorldwideLink();
      });
    }
    if (this.zipInputMobile) {
      this.zipInputMobile.addEventListener('input', () => {
        this.displayMobileContactFlyoutImage(this.zipInputMobile);
        this.handleZipInput(this.zipInputMobile, this.countrySelectMobile);
      });
    }
  }

  private displayMobileContactFlyoutImage(element: HTMLElement | null) {
    if (!element) {
      return;
    }
    const mobileCountryWrapper = element.parentElement;
    if (mobileCountryWrapper) {
      mobileCountryWrapper.dataset.showImage = '1';
    }
  }

  private applyCurrentCountry(): void {
    const countryCode = getCountryCodeNoneFiltered().toUpperCase();
    if (this.countrySelect) {
      if (Array.from(this.countrySelect.options).map(o => o.value).includes(countryCode)) {
        this.countrySelect.value = countryCode;
      } else {
        this.countrySelect.value = 'otherCountries';
      }
    }
    if (this.countrySelectMobile) {
      if (Array.from(this.countrySelectMobile.options).map(o => o.value).includes(countryCode)) {
        this.countrySelectMobile.value = countryCode;
      } else {
        this.countrySelectMobile.value = 'otherCountries';
      }
    }
  }

  private initSlimSelect() {
    if (this.countrySelect) {
      new SlimSelect({
        select: this.countrySelect,
        showSearch: true,
        searchPlaceholder: getSearchLabel(),
      });
    }
    if (this.countrySelectMobile) {
      new SlimSelect({
        select: this.countrySelectMobile,
        showSearch: true,
        searchPlaceholder: getSearchLabel(),
      });
    }
  }

  private handleZipInput(zipInput: HTMLInputElement | null, countrySelect: HTMLSelectElement | null) {
    if (!zipInput || !countrySelect) {
      return;
    }
    if (this.selectedCountry === '') {
      return;
    }
    if (!this.contactElements) {
      return;
    }

    let zipLength = 5;
    if (this.selectedCountry === 'ch') {
      zipLength = 4;
    }

    let zip: string | number = zipInput.value || '0';
    if (typeof zip === 'string') {
      let inputLength = zip.length;
      if (zip.charAt(0) === '0') {
        zipLength = zipLength - 1;
        inputLength = inputLength - 1;
      }
      // fill unfinished input (input: 12 --> 12000) according to ZIP length
      zip = <number>parseInt(zip, 10) * Math.pow(10, zipLength - inputLength);
    }

    let fallbackElement: HTMLElement | null = null;
    let elementShown = false;

    // This loop is inperformant, feel free to improve
    this.contactElements.forEach((ce: HTMLElement) => {
      if (countrySelect && ce.dataset.countries && ce.dataset.countries !== '') {
        const validCountries = ce.dataset.countries.split(',');
        if (validCountries.includes(countrySelect.value)) {
          if (this.isCompanyContact(ce)) {
            fallbackElement = ce;
          } else if (this.containsNeededZip(ce, zip)) {
            elementShown = true;
            this.showElement(ce);
            return;
          }
        }
      }

      this.hideElement(ce);
    });

    if (!elementShown && fallbackElement) {
      this.showElement(fallbackElement);
    }

    this.trackZipUsing();
  }

  private trackZipUsing() {
    if (!this.zipInputWasUsed) {
      (<any>window).dataLayer = (<any>window).dataLayer || [];
      (<any>window).dataLayer.push({
        'event': 'input_input',
        'param_element': 'contact_worldwide_header',
        'param_identify': 'postal_code',
      });

      this.zipInputWasUsed = true;
    }
  }

  private showCorrectContactElements(zipInput: HTMLInputElement | null, countrySelect: HTMLSelectElement | null): void {
    let elementShown = false;
    /* search for a non company contact with the selected country*/
    if (countrySelect && zipInput && this.contactElements) {
      this.selectedCountry = countrySelect.value.toLowerCase();
      if (this.zipInputCountries.includes(this.selectedCountry) && this.departmentId === '2') {
        this.showElement(zipInput);
      } else {
        this.hideElement(zipInput);
        this.contactElements.forEach((ce: HTMLElement) => {
          if (countrySelect && ce.dataset.countries && ce.dataset.countries !== '' && !elementShown) {
            const validCountries = ce.dataset.countries.split(',');
            if (validCountries.includes(countrySelect.value) && !this.isCompanyContact(ce)) {
              elementShown = true;
              this.showElement(ce);
              return;
            }
          }

          this.hideElement(ce);
        });
      }
    }

    /* if no contact element was found search for a companyContact to display */
    if (!elementShown && this.contactElements) {
      this.contactElements.forEach((ce: HTMLElement) => {
        if (countrySelect && ce.dataset.countries && ce.dataset.countries !== '' && !elementShown) {
          const validCountries = ce.dataset.countries.split(',');
          if (validCountries.includes(countrySelect.value) && this.isCompanyContact(ce)) {
            elementShown = true;
            this.showElement(ce);
            return;
          }
        }

        this.hideElement(ce);
      });
    }

    /* if still no contact was found, search for non company contacts with "otherCountries" */
    if (!elementShown && this.contactElements) {
      this.contactElements.forEach((ce: HTMLElement) => {
        if (countrySelect && ce.dataset.countries && ce.dataset.countries !== '') {
          const validCountries = ce.dataset.countries.split(',');
          if (validCountries.includes('otherCountries') && !this.isCompanyContact(ce)) {
            elementShown = true;
            this.showElement(ce);
            return;
          }
        }

        this.hideElement(ce);
      });
    }

    /* if there was still no contact found, search for a companyContact with "otherCountries" */
    if (!elementShown && this.contactElements) {
      this.contactElements.forEach((ce: HTMLElement) => {
        if (countrySelect && ce.dataset.countries && ce.dataset.countries !== '') {
          const validCountries = ce.dataset.countries.split(',');
          if (validCountries.includes('otherCountries') && this.isCompanyContact(ce)) {
            elementShown = true;
            this.showElement(ce);
            return;
          }
        }

        this.hideElement(ce);
      });
    }
  }

  private hideElement(element: HTMLElement): void {
    element.classList.add(this.hideClass);
  }

  private showElement(element: HTMLElement): void {
    element.classList.remove(this.hideClass);
  }

  private isCompanyContact(element: HTMLElement): boolean {
    const zipSetDataSet = element.dataset.ziprange;

    // In this iteration we only check if there is a zipset - if not, it's a personal contact and it only gets shown
    // when typing a ZIP code into the zip input field
    return typeof zipSetDataSet === 'undefined';
  }

  private containsNeededZip(element: HTMLElement, zip: string | number): boolean {
    const zipSetDataSet = element.dataset.ziprange || '';
    const zipSet: ZipRange[] = JSON.parse(zipSetDataSet);

    let containsZip = false;
    zipSet.forEach((zipRange) => {
      if (!containsZip && zip >= zipRange.min && zip <= zipRange.max) {
        containsZip = true;
      }
    });
    return containsZip;
  }

  private handleProductInquiryButton() {
    const productInquiryButton: HTMLAnchorElement | null = document.querySelector('.js-product-inquiry-button');
    const productDetail: HTMLElement | null = document.querySelector('.js-product-detail');
    if (!productInquiryButton || !productDetail) {
      return;
    }
    productInquiryButton.href += '#inquiry|' + productDetail.dataset.formsubject;
  }

  private updateWorldwideLink() {
    if (this.worldwideLinkElement && this.countrySelect && this.countrySelect.options[this.countrySelect.selectedIndex].value) {
      this.worldwideLinkElement.href = this.defaultWorldwideLink + '?country=' + this.countrySelect.options[this.countrySelect.selectedIndex].value;
    }
  }

  private unzip(element: HTMLElement | null) {
    if (!element) {
      return;
    }
    element.innerHTML = element.innerHTML
      .replace('<!--js-contactsFlyout', '')
      .replace('-->', '')
      .replace('--A>', '-->')
      .replace('--A2>', '--A>')
      .replace('--A3>', '--A2>')
      .replace('--A4>', '--A3>')
      .replace('--A5>', '--A4>')
    ;
  }
}

export interface ZipRange {
  min: number;
  max: number;
  contactPersonUid: number;
}
